import React, { useState } from 'react';
import { ArrowRight, Clock, Zap, Shield, Thermometer, TrendingUp, Battery, ChevronRight } from 'lucide-react';

const WhyNonLithiumSection = () => {
  const [activeScenario, setActiveScenario] = useState(0);

  const scenarios = [
    {
      id: 'warehouse',
      title: 'Warehouse Fleet',
      icon: '🏭',
      before: {
        title: 'With Lithium-Ion',
        points: [
          'Forklifts offline 8+ hours daily for charging',
          'Need 3 batteries per forklift for multi-shift',
          'Dedicated charging room with ventilation',
          'Battery swaps take 15-20 minutes each'
        ],
        stat: '60%',
        statLabel: 'Equipment utilization'
      },
      after: {
        title: 'With Multi-Ion',
        points: [
          'Charge during 15-minute breaks',
          'One battery handles all shifts',
          'Charge anywhere - no special rooms',
          'Zero battery swaps needed'
        ],
        stat: '95%',
        statLabel: 'Equipment utilization'
      }
    },
    {
      id: 'coldstorage',
      title: 'Cold Storage',
      icon: '❄️',
      before: {
        title: 'With Lithium-Ion',
        points: [
          'Capacity drops 40% at -20°C',
          'Batteries must be warmed before use',
          'Risk of sudden failure in freezers',
          'Separate heated charging area required'
        ],
        stat: '60%',
        statLabel: 'Capacity at -20°C'
      },
      after: {
        title: 'With Multi-Ion',
        points: [
          'Full capacity down to -40°C',
          'No warming required - instant operation',
          'Consistent performance in any temperature',
          'Charge directly inside cold storage'
        ],
        stat: '100%',
        statLabel: 'Capacity at -40°C'
      }
    },
    {
      id: 'backup',
      title: 'Backup Power',
      icon: '⚡',
      before: {
        title: 'With Lithium-Ion',
        points: [
          'Replace batteries every 5-7 years',
          'Special safety systems required',
          'Thermal monitoring adds complexity',
          'Higher insurance premiums'
        ],
        stat: '5-7',
        statLabel: 'Years lifespan'
      },
      after: {
        title: 'With Multi-Ion',
        points: [
          'Install once, run for 15+ years',
          'No special safety systems needed',
          'Inherently stable chemistry',
          'Lower insurance costs'
        ],
        stat: '15+',
        statLabel: 'Years lifespan'
      }
    }
  ];

  const currentScenario = scenarios[activeScenario];

  return (
    <section id="why-multi-ion" className="relative py-24 bg-gray-950 overflow-hidden" data-testid="why-multi-ion-section">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-emerald-900/20 via-gray-950 to-gray-950" />
      </div>
      
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
            <Zap className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-400 text-sm font-medium">The Multi-Ion Difference</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            See The <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">Transformation</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Real operations. Real impact. See how Multi-Ion changes the game.
          </p>
        </div>

        {/* Scenario Selector */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {scenarios.map((scenario, index) => (
            <button
              key={scenario.id}
              onClick={() => setActiveScenario(index)}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
                activeScenario === index
                  ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25'
                  : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800 hover:text-white border border-white/10'
              }`}
              data-testid={`scenario-${scenario.id}`}
            >
              <span className="text-xl">{scenario.icon}</span>
              <span>{scenario.title}</span>
            </button>
          ))}
        </div>

        {/* Before/After Comparison */}
        <div className="relative">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 z-10">
            <div className="relative flex items-center justify-center">
              <div className="absolute w-full h-0.5 bg-gradient-to-r from-red-500 via-gray-600 to-emerald-500" />
              <div className="relative bg-gray-900 border-2 border-gray-700 rounded-full p-3">
                <ChevronRight className="w-6 h-6 text-emerald-400" />
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
            {/* Before - Lithium */}
            <div className="relative group">
              <div className="absolute inset-0 bg-red-500/5 rounded-3xl blur-xl group-hover:bg-red-500/10 transition-all" />
              <div className="relative bg-gray-900/80 backdrop-blur-sm rounded-3xl p-8 border border-red-500/20 h-full">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-14 h-14 rounded-2xl bg-red-500/10 flex items-center justify-center">
                    <Battery className="w-7 h-7 text-red-400" />
                  </div>
                  <div>
                    <p className="text-red-400 text-sm font-medium uppercase tracking-wider">Before</p>
                    <h3 className="text-2xl font-bold text-white">{currentScenario.before.title}</h3>
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  {currentScenario.before.points.map((point, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-red-400 text-xs">✕</span>
                      </div>
                      <span className="text-gray-400">{point}</span>
                    </li>
                  ))}
                </ul>

                <div className="bg-red-500/10 rounded-2xl p-6 text-center border border-red-500/20">
                  <p className="text-5xl font-bold text-red-400 mb-1">{currentScenario.before.stat}</p>
                  <p className="text-gray-500 text-sm">{currentScenario.before.statLabel}</p>
                </div>
              </div>
            </div>

            {/* After - Multi-Ion */}
            <div className="relative group">
              <div className="absolute inset-0 bg-emerald-500/5 rounded-3xl blur-xl group-hover:bg-emerald-500/10 transition-all" />
              <div className="relative bg-gray-900/80 backdrop-blur-sm rounded-3xl p-8 border border-emerald-500/30 h-full">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
                    <Zap className="w-7 h-7 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-emerald-400 text-sm font-medium uppercase tracking-wider">After</p>
                    <h3 className="text-2xl font-bold text-white">{currentScenario.after.title}</h3>
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  {currentScenario.after.points.map((point, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-emerald-400 text-xs">✓</span>
                      </div>
                      <span className="text-gray-300">{point}</span>
                    </li>
                  ))}
                </ul>

                <div className="bg-emerald-500/10 rounded-2xl p-6 text-center border border-emerald-500/20">
                  <p className="text-5xl font-bold text-emerald-400 mb-1">{currentScenario.after.stat}</p>
                  <p className="text-emerald-400/70 text-sm">{currentScenario.after.statLabel}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Differentiators */}
        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: Clock, value: '15 min', label: 'Full Charge', color: 'emerald' },
            { icon: TrendingUp, value: '5,000+', label: 'Cycles', color: 'cyan' },
            { icon: Shield, value: 'UN 38.3', label: 'Certified', color: 'blue' },
            { icon: Thermometer, value: '-40°C', label: 'to 65°C', color: 'purple' },
          ].map((item, index) => {
            const Icon = item.icon;
            return (
              <div 
                key={index}
                className="bg-gray-900/50 rounded-2xl p-6 text-center border border-white/5 hover:border-emerald-500/30 transition-all"
              >
                <div className={`w-12 h-12 rounded-xl bg-${item.color}-500/10 flex items-center justify-center mx-auto mb-4`}>
                  <Icon className={`w-6 h-6 text-${item.color}-400`} />
                </div>
                <p className="text-2xl font-bold text-white mb-1">{item.value}</p>
                <p className="text-gray-500 text-sm">{item.label}</p>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <a
            href="#products"
            className="inline-flex items-center gap-3 bg-emerald-500 hover:bg-emerald-400 text-white px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/25"
            data-testid="why-multiion-cta"
          >
            Explore Our Products
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default WhyNonLithiumSection;
